import { PushNotifications, type ActionPerformed, type PushNotificationSchema } from "@capacitor/push-notifications";
import { Capacitor, type PermissionState } from "@capacitor/core";

/**
 * PUSH — the one capability here that cannot be faked on the web, and the one with the
 * most ways to fail silently.
 *
 * THE TRAP THAT COSTS AN AFTERNOON: a device token belongs to a BUNDLE ID and to an APNs
 * environment. The three identities of this app (dev · staging · production) are three
 * bundle ids, so they hold three different tokens for the same phone — and dev/staging
 * builds are signed for Apple's SANDBOX push service while a store build is not. Sending a
 * sandbox token to the production service is not an error you can see: Apple accepts it and
 * nothing arrives.
 *
 * So the token is never sent to the service alone. `registerForPush` reports the app's
 * environment with it, the service stores it on the row, and the sender picks Apple's host
 * from that column. See api/src/shared/push.ts and db/schemas/tables/device_tokens.sql.
 */
export type PushRegistration =
  | { status: "registered"; token: string }
  /** The user said no. Nothing in the app can undo this — only iOS Settings can. */
  | { status: "blocked" }
  /** Permission was given and the machinery failed anyway. Retryable, and NOT the user's fault. */
  | { status: "failed"; reason: "apns" | "timeout" | "dismissed" }
  | { status: "unsupported" };

/** What iOS currently thinks, as opposed to what happened last time we asked. */
export type PushPermission = "granted" | "blocked" | "askable" | "unsupported";

export type PushTapHandler = (data: Record<string, unknown>) => void;

const supported = Capacitor.isNativePlatform();

/**
 * `prompt` and `prompt-with-rationale` both mean NOT YET ASKED — they are not a refusal, and
 * treating them as one tells someone to go fix a setting that is already correct.
 */
function toPermission(receive: PermissionState): "granted" | "blocked" | "askable" {
  if (receive === "granted") return "granted";
  if (receive === "denied") return "blocked";
  return "askable";
}

/**
 * Reads the live permission without showing anything. A screen that offers to turn
 * notifications on has to open in the state the phone is actually in: already on, refused
 * long ago, or never asked. Asking iOS is the only way to know — the last answer this app
 * saw can be months stale, and the user may have changed it in Settings since.
 */
export async function getPushPermission(): Promise<PushPermission> {
  if (!supported) return "unsupported";
  const status = await PushNotifications.checkPermissions().catch(() => null);
  return status ? toPermission(status.receive) : "askable";
}

/**
 * Asks, then registers. Returns the token rather than firing a callback so the caller can
 * `await` it in the same place it decides what to do with a refusal — a permission prompt
 * shown at a moment the user does not understand is a permanent no.
 *
 * Safe to call when permission is already granted: iOS answers from its own record without
 * showing anything, which is how a token gets refreshed.
 */
export async function registerForPush(): Promise<PushRegistration> {
  if (!supported) return { status: "unsupported" };

  const decision = toPermission((await PushNotifications.requestPermissions()).receive);
  if (decision === "blocked") return { status: "blocked" };
  if (decision !== "granted") return { status: "failed", reason: "dismissed" };

  let settled = false;
  let done!: (result: PushRegistration) => void;
  const outcome = new Promise<PushRegistration>((resolve) => {
    done = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
  });

  // THE LISTENERS MUST BE ATTACHED BEFORE register() IS CALLED — and `addListener` only
  // finishes attaching when its promise resolves. Firing register() first is a race the
  // phone usually WINS: APNs answers a repeat registration out of its cache in a couple of
  // milliseconds, the token event lands with nobody listening, and the only thing left to
  // settle this promise is the timeout below. That is how someone who tapped Allow ended up
  // being told they had blocked notifications.
  const subs = await Promise.all([
    PushNotifications.addListener("registration", (token) =>
      done({ status: "registered", token: token.value }),
    ),
    PushNotifications.addListener("registrationError", () => done({ status: "failed", reason: "apns" })),
  ]);

  // APNs is a round trip to Apple. A phone in airplane mode never answers, and a promise
  // that never settles would hang whatever awaits it — usually app start.
  const expiry = setTimeout(() => done({ status: "failed", reason: "timeout" }), 10_000);

  void PushNotifications.register().catch(() => done({ status: "failed", reason: "apns" }));

  try {
    return await outcome;
  } finally {
    clearTimeout(expiry);
    // Every call would otherwise leave two more handlers behind, so a second attempt
    // resolves the first attempt's promise as well.
    await Promise.all(subs.map((sub) => sub.remove())).catch(() => {});
  }
}

/** Notifications that arrive while the app is open and in front. */
export function onPushReceived(handler: (n: PushNotificationSchema) => void): () => void {
  if (!supported) return () => {};
  const sub = PushNotifications.addListener("pushNotificationReceived", handler);
  return () => void sub.then((h) => h.remove());
}

/**
 * A TAP IS A DEEP LINK. The payload carries the same `path` a universal link would, so a
 * notification, a link in an email and a pasted URL all land on one screen — see
 * src/app/lib/deep-links.ts.
 */
export function onPushTapped(handler: PushTapHandler): () => void {
  if (!supported) return () => {};
  const sub = PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action: ActionPerformed) => handler(action.notification.data ?? {}),
  );
  return () => void sub.then((h) => h.remove());
}

/** iOS shows a badge until it is cleared. Clearing on foreground is the honest default. */
export async function clearDeliveredNotifications(): Promise<void> {
  if (!supported) return;
  await PushNotifications.removeAllDeliveredNotifications().catch(() => {});
}
